import MonitorNavigation from '../../components/monitor/MonitorNavigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import {
  Box, Container, Grid, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import LocationHeader from '../../components/mobile_scan/LocationHeader';
import ScanCards from '../../components/mobile_scan/ScanCards';
import SummaryCard from '../../components/mobile_scan/SummaryCard';
import ItemListCard from '../../components/mobile_scan/ItemListCard';
import ScanActionModal from '../../components/mobile_scan/ScanActionModal';
import useCheckToken from '../../hooks/useCheckToken';
import useExchangeToken from '../../hooks/useExchangeToken';
import {
  createWipMobileScan, deleteWipMobileScan, getWipMasterOptions,
  getWipMobileScans, getWipRegistration, getWipRegistrations,
} from '../../services/wipTrackingApi';
import { extractApiList } from '../../utils/wipTracking';
import {
  apiErrorMessage, findRegistrationForScan, mobileScanStatusCounts, parseWipScanCode,
} from '../../utils/mobileScan';

const same = (left, right) => String(left ?? '').trim().toUpperCase() === String(right ?? '').trim().toUpperCase();
const isMesAccessToken = (token) => {
  try {
    const claims = jwtDecode(token);
    return claims?.token_type === 'access' && Number(claims?.exp) > Date.now() / 1000 + 60;
  } catch {
    return false;
  }
};

export default function MobileScan() {
  useCheckToken();
  const exchangeToken = useExchangeToken();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const {
    bu: routeBu = '', plant: routePlant = '', factory: routeFactory = '',
  } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const portalAccessToken = useSelector((state) => state.auth?.accessToken || '');
  const authAccessToken = useSelector((state) => state.auth?.access_token || '');
  const authBu = useSelector((state) => state.auth?.bu || '');
  const authPlantId = useSelector((state) => state.auth?.plant_id || '');
  const authFactory = useSelector((state) => state.auth?.factory || '');
  const operator = useSelector((state) => state.auth?.display || state.auth?.email || '');
  const bu = routeBu || authBu;
  const plantId = routePlant || authPlantId;
  const factory = routeFactory || authFactory;

  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [scans, setScans] = useState([]);
  const [query, setQuery] = useState('');
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [backendToken, setBackendToken] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const processingRef = useRef(false);
  const exchangedPortalTokenRef = useRef('');

  const scope = useMemo(() => ({ plant_id: plantId, factory }), [factory, plantId]);
  const scopeReady = Boolean(backendToken && plantId && factory);
  const counts = useMemo(() => mobileScanStatusCounts(registrations), [registrations]);

  const notify = useCallback((message, severity = 'error') => {
    setToast({ open: true, message, severity });
  }, []);

  useEffect(() => {
    let active = true;
    if (isMesAccessToken(authAccessToken)) {
      setBackendToken(authAccessToken);
      setAuthLoading(false);
      return () => { active = false; };
    }
    if (!portalAccessToken || exchangedPortalTokenRef.current === portalAccessToken) {
      return () => { active = false; };
    }

    exchangedPortalTokenRef.current = portalAccessToken;
    setAuthLoading(true);
    exchangeToken({ force: true })
      .then((token) => {
        if (!active) return;
        if (!token) throw new Error('Unable to obtain MES access token.');
        setBackendToken(token);
      })
      .catch(() => {
        if (active) {
          exchangedPortalTokenRef.current = '';
          notify('ไม่สามารถยืนยันตัวตนกับ MES ได้ กรุณาเข้าสู่ระบบใหม่');
        }
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });
    return () => { active = false; };
  }, [authAccessToken, exchangeToken, notify, portalAccessToken]);

  const callWithAuth = useCallback(async (request) => {
    let token = isMesAccessToken(backendToken)
      ? backendToken
      : await exchangeToken({ force: true });
    if (!token) throw new Error('MES access token is unavailable.');
    try {
      return await request(token);
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
      token = await exchangeToken({ force: true });
      if (!token) throw error;
      setBackendToken(token);
      return request(token);
    }
  }, [backendToken, exchangeToken]);

  useEffect(() => {
    if (routeLocation.pathname !== '/wip-tracking/mobile-scan' || !bu || !plantId || !factory) return;
    const path = [bu, plantId, factory, 'WIP Tracking', 'mobile-scan']
      .map((part) => encodeURIComponent(part))
      .join('/');
    navigate(`/${path}${routeLocation.search}`, { replace: true });
  }, [bu, factory, navigate, plantId, routeLocation.pathname, routeLocation.search]);

  useEffect(() => {
    let active = true;
    if (!scopeReady) return undefined;
    setLoadingOptions(true);
    callWithAuth((token) => getWipMasterOptions({ plant_id: plantId, factory }, token))
      .then((response) => {
        if (!active) return;
        const options = extractApiList(response.data)[0] || {};
        const values = Array.isArray(options.storage_locations) ? options.storage_locations.filter(Boolean) : [];
        setLocations(values);
        setLocation((current) => (values.includes(current) ? current : (values[0] || '')));
      })
      .catch((error) => notify(apiErrorMessage(error, 'ไม่สามารถโหลดรายการสถานที่ได้')))
      .finally(() => active && setLoadingOptions(false));
    return () => { active = false; };
  }, [callWithAuth, factory, notify, plantId, scopeReady]);

  const loadLocationData = useCallback(async () => {
    if (!scopeReady || !location) {
      setRegistrations([]);
      setScans([]);
      return;
    }
    setLoadingItems(true);
    try {
      const params = { ...scope, location };
      const [registrationResponse, scanResponse] = await Promise.all([
        callWithAuth((token) => getWipRegistrations({
          ...params, in_stock: true, page_size: 200, ordering: '-created_at',
        }, token)),
        callWithAuth((token) => getWipMobileScans(params, token)),
      ]);
      setRegistrations(extractApiList(registrationResponse.data));
      setScans(extractApiList(scanResponse.data).slice(0, 10));
    } catch (error) {
      notify(apiErrorMessage(error, 'ไม่สามารถโหลดข้อมูล Mobile Scan ได้'));
    } finally {
      setLoadingItems(false);
    }
  }, [callWithAuth, location, notify, scope, scopeReady]);

  useEffect(() => { loadLocationData(); }, [loadLocationData]);

  const ensureCanScan = () => {
    if (!scopeReady) {
      notify(authLoading ? 'กำลังยืนยันตัวตน กรุณารอสักครู่' : 'ไม่พบ plant หรือ factory ของผู้ใช้งาน');
      return false;
    }
    if (!location) {
      notify('กรุณาเลือกสถานที่ก่อนสแกน', 'warning');
      return false;
    }
    return true;
  };

  const openCamera = () => {
    if (!ensureCanScan()) return;
    setIsScanModalOpen(true);
  };

  const resolveRegistration = async (parsed) => {
    let registration = findRegistrationForScan(registrations, parsed, location);
    if (!registration && parsed.id) {
      const response = await callWithAuth((token) => getWipRegistration(
        parsed.id,
        token,
        { ...scope, location, in_stock: true },
      ));
      registration = response.data?.data || response.data;
    }
    if (!registration && parsed.matCode) {
      const response = await callWithAuth((token) => getWipRegistrations({
        ...scope, location, in_stock: true, search: parsed.matCode, page_size: 200,
      }, token));
      registration = findRegistrationForScan(extractApiList(response.data), parsed, location);
    }
    return registration;
  };

  const handleScan = async (rawCode, scanMode = 'laser') => {
    if (!ensureCanScan()) return;
    if (processingRef.current) return;
    processingRef.current = true;
    setScanning(true);
    try {
      const parsed = parseWipScanCode(rawCode);
      if (!parsed) throw new Error('QR Code ไม่ใช่รูปแบบ WIP Tracking');
      if (scans.some((item) => item.scan_code === parsed.raw)) {
        throw new Error('QR Code นี้ถูกสแกนออกแล้ว');
      }

      const registration = await resolveRegistration(parsed);
      if (!registration) throw new Error('ไม่พบข้อมูล Register ของ QR Code นี้');
      if (!same(registration.plant_id, plantId) || !same(registration.factory, factory)) {
        throw new Error('สินค้าไม่ได้อยู่ใน Plant / Factory ของผู้ใช้งาน');
      }
      if (!same(registration.location, location)) {
        throw new Error(`สินค้าอยู่ที่ ${registration.location || 'สถานที่อื่น'} กรุณาเลือกสถานที่ให้ถูกต้อง`);
      }
      if (scans.some((item) => String(item.source_register_id) === String(registration.id))) {
        throw new Error('สินค้ารายการนี้ถูกสแกนออกแล้ว');
      }

      const response = await callWithAuth((token) => createWipMobileScan({
        plant_id: plantId,
        factory,

        location,
        scan_mode: scanMode,
        scan_code: parsed.raw,
        product_name: registration.product_name,
        lot: registration.lot,
        batch: registration.batch,
        weight: Number(registration.weight || 0),
        shift: registration.shift,
        qty: Number(registration.qty || 0),
        source_register_id: registration.id,
        scanned_at: new Date().toISOString(),
        status: 'READY',
        created_by: operator || 'WIP Mobile Scan',
      }, token));
      const created = response.data?.data || response.data;
      setScans((current) => [created, ...current.filter((item) => item.id !== created.id)].slice(0, 10));
      setRegistrations((current) => current.filter((item) => String(item.id) !== String(registration.id)));
      setIsScanModalOpen(false);
      notify(`สแกนออกสำเร็จ: ${registration.product_name}`, 'success');
      if (navigator.vibrate) navigator.vibrate(120);
    } catch (error) {
      notify(apiErrorMessage(error, 'สแกนสินค้าไม่สำเร็จ'));
      if (navigator.vibrate) navigator.vibrate([80, 50, 80]);
    } finally {
      processingRef.current = false;
      setScanning(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await callWithAuth((token) => deleteWipMobileScan(deleteTarget.id, token));
      await loadLocationData();
      notify('ยกเลิกรายการสแกนแล้ว สามารถสแกนสินค้าใบนี้ใหม่ได้', 'success');
      setDeleteTarget(null);
    } catch (error) {
      notify(apiErrorMessage(error, 'ไม่สามารถยกเลิกรายการสแกนได้'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.primary[900], pb: 6 }}>
      <Container maxWidth={false} sx={{ mt: { xs: 1.5, md: 2.25 }, maxWidth: '1460px' }}>
        <MonitorNavigation />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LocationHeader location={location} locations={locations} onLocationChange={setLocation} loading={loadingOptions} />
          </Grid>
          <Grid item xs={12} md={8}>
            <ScanCards
              onScan={(code) => handleScan(code, 'laser')}
              onCameraClick={openCamera}
              busy={scanning}
              disabled={!scopeReady || !location}
            />
          </Grid>
          <Grid item xs={12} md={4}><SummaryCard counts={counts} /></Grid>
          <Grid item xs={12}>
            <ItemListCard
              items={scans}
              query={query}
              onQueryChange={setQuery}
              location={location}
              loading={loadingItems}
              onDelete={setDeleteTarget}
              deletingId={deletingId}
            />
          </Grid>
        </Grid>
      </Container>

      <ScanActionModal
        open={isScanModalOpen}
        busy={scanning}
        onClose={() => !scanning && setIsScanModalOpen(false)}
        onScan={(code) => handleScan(code, 'camera')}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deletingId && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>ยืนยันการยกเลิกรายการสแกน</DialogTitle>
        <DialogContent>
          <Typography>ต้องการนำ “{deleteTarget?.product_name || '-'}” กลับเข้ารายการที่สแกนได้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)}>ไม่ยกเลิก</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={Boolean(deletingId)}>ยืนยัน</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'success' ? 2800 : 5000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))} sx={{ fontSize: 16, fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
